import React, { Fragment } from 'react'
import { Timeline, Typography } from 'antd'
import { includes, map } from 'lodash'

import { EVENT_TYPES } from '../../common/constants'
import { eventDifference, getEventDifferenceText } from '../../common/helpers'
import { formatCreatedByText } from '../../common/utils'

const transformCaseDefinition = (caseDefinition, ingredientsGroupedById) => ({
  ...caseDefinition,
  exclude_rule: caseDefinition.exclude_rule?.id
    ? caseDefinition.exclude_rule
    : ingredientsGroupedById[caseDefinition.exclude_rule]?.[0],

  target_ingredient: caseDefinition.target_ingredient?.id
    ? caseDefinition.target_ingredient
    : ingredientsGroupedById[caseDefinition.target_ingredient]?.[0],

  default_ingredients: map(caseDefinition.default_ingredients, item => {
    if (item.id) {
      return {
        ...item,
        name: item.name,
      }
    }

    if (!ingredientsGroupedById[item]?.[0]) {
      return {
        ...item,
        name: `${item} (deleted) from db`,
      }
    }

    return ingredientsGroupedById[item]?.[0]
  }),

  ingredients_to_check: map(caseDefinition.ingredients_to_check, item => {
    if (item.id) {
      return {
        ...item,
        name: item.name,
      }
    }

    if (!ingredientsGroupedById[item]?.[0]) {
      return {
        id: item,
        name: `${item} (deleted) from db`,
      }
    }

    return ingredientsGroupedById[item]?.[0]
  }),
})

const renderIngredientSpecialCaseEvent = (
  event,
  ingredientsGroupedById,
  onClick,
) => {
  const { type, original, changes, createdAt, createdBy } = event

  if (type !== EVENT_TYPES.UPDATE) {
    return (
      <Timeline.Item key={event.id}>
        <Typography.Text>
          {type === EVENT_TYPES.CREATE ? 'Created' : 'Deleted'} {changes.type}{' '}
          {type === EVENT_TYPES.CREATE ? changes.name : original.name}
        </Typography.Text>
        <br />
        <Typography.Text>
          {formatCreatedByText(createdAt, createdBy.name)}
        </Typography.Text>
      </Timeline.Item>
    )
  }

  const eventDifferences = eventDifference(original, changes, 'name', [
    'caseDefinition',
  ])

  const caseDefinitionDifferences = eventDifference(
    transformCaseDefinition(original.caseDefinition, ingredientsGroupedById),
    transformCaseDefinition(changes.caseDefinition, ingredientsGroupedById),
    'name',
  )

  const mappedCaseDefinitionDifferences = map(
    caseDefinitionDifferences,
    ({ differentKey, original, change }) => {
      if (includes(['exclude_rule', 'target_ingredient'], differentKey)) {
        return {
          differentKey,
          original:
            original?.name || ingredientsGroupedById[original]?.[0]?.name,
          change: change?.name || ingredientsGroupedById[change]?.[0]?.name,
        }
      }

      if (
        includes(['default_ingredients', 'ingredients_to_check'], differentKey)
      ) {
        return {
          differentKey,
          original: map(original, item => {
            if (item.id) {
              return item.name
            }

            return ingredientsGroupedById[item]?.[0]?.name
          }),
          change: map(change, item => {
            if (item.id) {
              return item.name
            }

            return ingredientsGroupedById[item]?.[0]?.name
          }),
        }
      }

      return { differentKey, original, change }
    },
  )

  return (
    <Timeline.Item key={event.id}>
      <Typography.Text>Updated {original.name}:</Typography.Text>
      <br />
      {map(eventDifferences, ({ differentKey, original, change }) => (
        <Fragment key={differentKey}>
          <Typography.Text>
            {getEventDifferenceText(differentKey, original, change)}
          </Typography.Text>
          <br />
        </Fragment>
      ))}
      {map(
        mappedCaseDefinitionDifferences,
        ({ differentKey, original, change }) => (
          <Fragment key={differentKey}>
            <Typography.Text>
              {getEventDifferenceText(
                differentKey,
                original,
                change,
                true,
                onClick,
              )}
            </Typography.Text>
            <br />
          </Fragment>
        ),
      )}
      <Typography.Text>
        {formatCreatedByText(createdAt, createdBy.name)}
      </Typography.Text>
    </Timeline.Item>
  )
}

export default renderIngredientSpecialCaseEvent
